import type { RequestBody } from '@vrplatform/data';
import { bankersRound, day } from '@vrplatform/utils';

export type ReservationReclassificationArgs = {
  existing: {
    lineId: string;
    centTotal: number;
    description: string;
    type: string;
  };
  updated: {
    lineType: string;
    description: string;
    centTotal: number;
    postingAt: string | undefined;
  };
};

type ReservationUpdateBody = RequestBody<'put:/reservations/{id}'>;

const mapPostingAt = (postingAt: string | null | undefined) =>
  postingAt ? day(postingAt).yyyymmdd() : undefined;

export const buildReservationReclassifyMutationBody = ({
  existing,
  updated,
}: ReservationReclassificationArgs): ReservationUpdateBody => {
  const existingCentTotal = bankersRound(existing.centTotal);
  const updatedCentTotal = bankersRound(updated.centTotal);
  const nextSourceCentTotal = existingCentTotal - updatedCentTotal;
  const postingAt = mapPostingAt(updated.postingAt);

  const updatedDescription = updated.description.trim() || null;
  const existingDescription = existing.description.trim() || null;

  if (Math.abs(updatedCentTotal) > Math.abs(existingCentTotal)) {
    throw new Error('Reclassified amount cannot exceed source amount');
  }

  if (nextSourceCentTotal === 0) {
    const isTypeDifferent = existing.type !== updated.lineType;
    const isDescriptionDifferent = existingDescription !== updatedDescription;
    const hasPostingAtChange = postingAt !== undefined;

    if (!isTypeDifferent && !isDescriptionDifferent && !hasPostingAtChange) {
      throw new Error(
        'Full reclassification must change line type, description, or posting date'
      );
    }

    return {
      lines_update: [
        {
          id: existing.lineId,
          type: isTypeDifferent ? updated.lineType : undefined,
          description: isDescriptionDifferent ? updatedDescription : undefined,
          generalLedgerPostingAt: hasPostingAtChange ? postingAt : undefined,
        },
      ],
    };
  }

  return {
    lines_create: [
      {
        type: updated.lineType,
        description: updatedDescription,
        amount: bankersRound(updated.centTotal),
        generalLedgerPostingAt: postingAt,
      },
    ],
    lines_update: [
      {
        id: existing.lineId,
        amount: nextSourceCentTotal,
      },
    ],
  };
};
